import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import {
  HGIntersection,
  HGAnyRecordSerialized,
  HGMe,
  HGProfile,
} from "@/common/types";
import { deserializeRecord } from "@/common/record";

export interface HGHoverRecordState {
  record: HGAnyRecordSerialized | null;
  intersection: HGIntersection | null;
}

export interface UIState {
  me: HGMe | null;
  mobileLayout: boolean;
  desktopLayout: boolean;
  viewingRecord: HGAnyRecordSerialized | null;
  /**
   * Key of the timeline row that opened the preview.
   *
   * A record spanning several intervals gets a row in each of them, and every
   * one of those reads as selected because selection is by record; this says
   * which one to close on.
   *
   * It lives here rather than in `RecordsTimeline` so a row can subscribe to
   * "am *I* the selected one" as a boolean. Held as component state it had to
   * travel down through the row context, whose identity then changed on every
   * click and re-rendered all several thousand rows.
   */
  selectedRecordKey: string | null;
  hoverRecord: HGHoverRecordState;
  showTransientRecords: boolean;
  userInfo: Record<string, string>;
  idToken: string | null;
  // One-shot requests, not open/closed state: the sidebar controllers clear
  // each back to false as soon as they act on it. Real open state lives in the
  // SidebarProvider that owns the panel. Named for the panel they actually
  // control — these were once "left"/"right" and were wired to the opposite
  // side of the screen from their names.
  collectionsSidebarRequested: boolean;
  timeSidebarRequested: boolean;
}

const userInfo = localStorage.getItem("userInfo");
const initialState: UIState = {
  me: null,
  mobileLayout: true,
  desktopLayout: false,
  viewingRecord: null,
  selectedRecordKey: null,
  hoverRecord: {
    record: null,
    intersection: null,
  },
  showTransientRecords: true,
  userInfo: userInfo ? JSON.parse(userInfo) : {},
  idToken: localStorage.getItem("idToken"),
  collectionsSidebarRequested: false,
  timeSidebarRequested: false,
};

export const uiSlice = createSlice({
  name: "UISlice",
  initialState,
  reducers: {
    setMe(state, action: PayloadAction<HGMe | null>) {
      state.me = action.payload;
    },
    // Patches just the profile fields after a profile mutation. Re-running
    // checkAuthorization() would also work but drags a full API cache reset
    // with it, for three strings.
    setMeProfile(state, action: PayloadAction<HGProfile>) {
      if (!state.me) return;
      state.me = { ...state.me, ...action.payload };
    },
    setLayout(state, action: PayloadAction<boolean>) {
      state.mobileLayout = action.payload;
      state.desktopLayout = !action.payload;
    },
    setViewingRecord: (
      state,
      action: PayloadAction<HGAnyRecordSerialized | null>,
    ) => {
      state.viewingRecord = action.payload;
      if (!action.payload) state.selectedRecordKey = null;
    },
    /**
     * Open (or close) the preview from a timeline row. One action rather than
     * two dispatches so a click costs a single store notification.
     */
    setViewingRecordFromRow: (
      state,
      action: PayloadAction<{
        record: HGAnyRecordSerialized | null;
        key: string;
      } | null>,
    ) => {
      const record = action.payload?.record ?? null;
      state.viewingRecord = record;
      // No record to show is a close, whichever row asked for it.
      state.selectedRecordKey = record ? (action.payload?.key ?? null) : null;
    },
    setHoverRecord: (
      state,
      action: PayloadAction<HGHoverRecordState | null>,
    ) => {
      if (action.payload) {
        state.hoverRecord = action.payload;
      } else {
        state.hoverRecord = initialState.hoverRecord;
      }
    },

    setShowTransientRecords: (state, action: PayloadAction<boolean>) => {
      state.showTransientRecords = action.payload;
    },
    setUserInfo: (state, action: PayloadAction<Record<string, string>>) => {
      state.userInfo = action.payload;
    },
    setIDToken: (state, action: PayloadAction<string | null>) => {
      state.idToken = action.payload;
    },
    requestOpenCollectionsSidebar: (state, action: PayloadAction<boolean>) => {
      state.collectionsSidebarRequested = action.payload;
    },
    requestOpenTimeSidebar: (state, action: PayloadAction<boolean>) => {
      state.timeSidebarRequested = action.payload;
    },
  },
});

export const {
  setMe,
  setMeProfile,
  setLayout,
  setViewingRecord,
  setViewingRecordFromRow,
  setHoverRecord,
  setShowTransientRecords,
  setUserInfo,
  setIDToken,
  requestOpenCollectionsSidebar,
  requestOpenTimeSidebar,
} = uiSlice.actions;

export const selectMe = (state: RootState) => state.ui.me;

export const selectViewingRecord = createSelector(
  (state: RootState) => state.ui.viewingRecord,
  (record: HGAnyRecordSerialized | null) => deserializeRecord(record),
);

/**
 * Per-row selection predicates. Booleans on purpose: a row subscribing to one
 * of these only re-renders when its own answer flips, so opening a preview
 * re-renders the two rows involved rather than the whole list.
 */
export const selectIsViewingRecordID = (recordID: string) => (state: RootState) =>
  state.ui.viewingRecord?.id === recordID;

export const selectIsSelectedRecordKey = (key: string) => (state: RootState) =>
  state.ui.selectedRecordKey === key;

export const selectHoverRecordState = createSelector(
  (state: RootState) => state.ui.hoverRecord,
  (hoverState: HGHoverRecordState) => {
    return {
      record: deserializeRecord(hoverState.record),
      intersection: hoverState.intersection,
    };
  },
);

export const selectShowTransientRecords = (state: RootState) =>
  state.ui.showTransientRecords;

export const selectMobileLayout = (state: RootState) => state.ui.mobileLayout;
export const selectDesktopLayout = (state: RootState) => state.ui.desktopLayout;

export const selectIDToken = (state: RootState) => state.ui.idToken;
export const selectUserInfo = (state: RootState) => state.ui.userInfo;

export const selectCollectionsSidebarRequested = (state: RootState) =>
  state.ui.collectionsSidebarRequested;
export const selectTimeSidebarRequested = (state: RootState) =>
  state.ui.timeSidebarRequested;

export default uiSlice.reducer;
